// src/components/NotificationConfig/forms/TelegramConfigForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRobot,
    faComments,
    faCheck,
    faTimes,
    faEye,
    faEyeSlash,
    faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { useNotificationContext } from '../../../contexts/NotificationContext';

const TelegramConfigForm = ({ marcaId }) => {
    const { getChannelConfig, updateConfig, loading } = useNotificationContext();
    const [config, setConfig] = useState({
        enabled: false,
        botToken: '',
        chatId: '',
        botUsername: '',
        webhookUrl: ''
    });
    const [showToken, setShowToken] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [isTesting, setIsTesting] = useState(false);

    const loadConfig = useCallback(() => {
        const telegramConfig = getChannelConfig('telegram');
        setConfig({
            enabled: telegramConfig.enabled || false,
            botToken: telegramConfig.botToken || '',
            chatId: telegramConfig.chatId || '',
            botUsername: telegramConfig.botUsername || '',
            webhookUrl: telegramConfig.webhookUrl || ''
        });
    }, [getChannelConfig]);

    useEffect(() => {
        if (marcaId) {
            loadConfig();
        }
    }, [marcaId, loadConfig]);

    const handleChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await updateConfig(marcaId, {
                telegram: config
            });

            setTestResult({ success: true, message: 'Configuración guardada correctamente' });
            setTimeout(() => setTestResult(null), 3000);
        } catch (error) {
            setTestResult({ success: false, message: error.message });
        }
    };

    const handleTestConnection = async () => {
        setIsTesting(true);
        setTestResult(null);

        try {
            const response = await fetch('/api/notifications/test-telegram', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth-storage-petitprime')}`
                },
                body: JSON.stringify({
                    marcaId,
                    config
                })
            });

            const result = await response.json();

            if (response.ok) {
                setTestResult({ success: true, message: 'Mensaje de prueba enviado correctamente a Telegram' });
            } else {
                setTestResult({ success: false, message: result.message || 'Error enviando mensaje de prueba' });
            }
        } catch (error) {
            setTestResult({ success: false, message: 'Error de conexión' });
        } finally {
            setIsTesting(false);
        }
    };

    const openTelegramBotHelp = () => {
        window.open('https://core.telegram.org/bots#how-do-i-create-a-bot', '_blank');
    };

    const openChatIdHelp = () => {
        window.open('https://stackoverflow.com/questions/32423837/telegram-bot-how-to-get-a-group-chat-id', '_blank');
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
                <FontAwesomeIcon icon={faTelegram} className="text-blue-500 text-xl mr-3" />
                <h3 className="text-lg font-semibold text-gray-800">Configuración de Telegram</h3>
                <div className="ml-auto">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={config.enabled}
                            onChange={(e) => handleChange('enabled', e.target.checked)}
                            className="sr-only"
                        />
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? 'bg-blue-500' : 'bg-gray-300'
                            }`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                            {config.enabled ? 'Habilitado' : 'Deshabilitado'}
                        </span>
                    </label>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Información de ayuda */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">
                        <FontAwesomeIcon icon={faComments} className="mr-2" />
                        Configuración de Bot de Telegram
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                        Para configurar las notificaciones de Telegram, necesitas crear un bot y obtener su token.
                    </p>
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={openTelegramBotHelp}
                            className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center"
                        >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                            Cómo crear un bot
                        </button>
                        <button
                            type="button"
                            onClick={openChatIdHelp}
                            className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center"
                        >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                            Obtener Chat ID
                        </button>
                    </div>
                </div>

                {/* Token del Bot */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FontAwesomeIcon icon={faRobot} className="mr-2" />
                        Token del Bot
                    </label>
                    <div className="relative">
                        <input
                            type={showToken ? 'text' : 'password'}
                            value={config.botToken}
                            onChange={(e) => handleChange('botToken', e.target.value)}
                            placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                        <button
                            type="button"
                            onClick={() => setShowToken(!showToken)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            disabled={!config.enabled}
                        >
                            <FontAwesomeIcon icon={showToken ? faEyeSlash : faEye} />
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Token obtenido de @BotFather en Telegram
                    </p>
                </div>

                {/* Chat ID */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Chat ID
                    </label>
                    <input
                        type="text"
                        value={config.chatId}
                        onChange={(e) => handleChange('chatId', e.target.value)}
                        placeholder="-1001234567890"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!config.enabled}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        ID del chat o canal donde se enviarán las notificaciones
                    </p>
                </div>

                {/* Username del Bot */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username del Bot
                    </label>
                    <input
                        type="text"
                        value={config.botUsername}
                        onChange={(e) => handleChange('botUsername', e.target.value)}
                        placeholder="mi_bot"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!config.enabled}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Nombre de usuario del bot (sin @)
                    </p>
                </div>

                {/* Webhook URL (opcional) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Webhook URL (Opcional)
                    </label>
                    <input
                        type="url"
                        value={config.webhookUrl}
                        onChange={(e) => handleChange('webhookUrl', e.target.value)}
                        placeholder="https://tu-dominio.com/webhook/telegram"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!config.enabled}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        URL para recibir actualizaciones de Telegram (opcional)
                    </p>
                </div>

                {/* Mensaje de resultado */}
                {testResult && (
                    <div className={`p-3 rounded-md ${testResult.success
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                        <div className="flex items-center">
                            <FontAwesomeIcon
                                icon={testResult.success ? faCheck : faTimes}
                                className={`mr-2 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}
                            />
                            {testResult.message}
                        </div>
                    </div>
                )}

                {/* Botones */}
                <div className="flex space-x-3 pt-4">
                    <button
                        type="submit"
                        disabled={loading || !config.enabled}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Guardando...' : 'Guardar Configuración'}
                    </button>

                    <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={isTesting || !config.enabled || !config.botToken || !config.chatId}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isTesting ? 'Enviando...' : 'Enviar Mensaje de Prueba'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TelegramConfigForm; 